// 表へ送る op を、表ごとに 1 件ずつ順番に送るための送信キュー。
//
// op は種別ごとに別 Lambda で処理されるため、送信順に確定する保証が無い。表ではこれが 2 つの形で
// 表面化する。
//  - Tab による行の自動追加の直後に入力すると、cell.update が insert-row より先に着き、
//    範囲外として扱われる。
//  - 同じセルへ 50ms ごとに送る連続入力が入れ替わり、古い本文が新しい本文を上書きする
//    （単独の利用者が普通に打っているだけで起きる）。
//
// これをクライアント側の送信の直列化で解く。サーバへ依存ヒントや順序番号を持ち込まない。
// 楽観適用は待たない（画面は即座に更新する）。ここが担うのは「送信の順序と 1 件ずつの待機」だけで、
// 楽観適用・巻き戻しは従来どおり doc store が行う。

import type { UpdateTableCellOp, UpdateTableShapeOp } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";

/** 送信を直列化する対象の op。 */
export type TableQueuedOp = UpdateTableCellOp | UpdateTableShapeOp;

/**
 * 表 1 つあたりの待機上限（最大 1,000 セル + 構造操作の余裕 128 件）。
 * 上限到達後の新しい操作は楽観適用前に拒否して通知し、既存の待機列を消さない。
 */
export const TABLE_QUEUE_MAX_PENDING = 1_128;

/** 応答（op.commit / op.reject）を待つ期限。超過したら後続送信を止めて再同期へ移る。 */
export const TABLE_QUEUE_RESPONSE_TIMEOUT_MS = 10_000;

/** 応答期限の監視間隔。期限そのものより十分に細かく刻む。 */
const TABLE_QUEUE_DEADLINE_TICK_MS = 1_000;

/** キューが呼び出し元へ依頼する操作。テスト時は差し替えられるよう関数の集合で受け取る。 */
export interface TableOpQueueHost {
  /** 実際に op を送信する（dispatcher の realtime.sendOp）。 */
  readonly send: (op: TableQueuedOp) => void;
  /** 統合・破棄した op の楽観適用を取り消す。 */
  readonly dropOptimistic: (clientOpId: string) => void;
  /** 利用者への通知（未保存・上限・期限切れ）。 */
  readonly notify: (message: string) => void;
  /** 権威スナップショットの取り直しを要求する。 */
  readonly requestResync: () => void;
}

interface QueueEntry {
  /** 送信済みで応答待ちの op。1 表につき常に 1 件以下。 */
  inFlight: TableQueuedOp | null;
  /** 応答待ちの絶対期限（epoch ms）。 */
  deadlineAt: number | null;
  /** 未送信の op（FIFO）。 */
  pending: TableQueuedOp[];
}

const queues = new Map<string, QueueEntry>();
let host: TableOpQueueHost | null = null;
let unsubscribeDocStore: (() => void) | null = null;
let deadlineTimer: ReturnType<typeof setInterval> | null = null;

/** 表ごとの待機状態（テストと未保存表示の判定に使う読み取り専用のスナップショット）。 */
export function tableOpQueueSnapshot(objectId: string): {
  readonly inFlightClientOpId: string | null;
  readonly pendingCount: number;
} {
  const entry = queues.get(objectId);
  return {
    inFlightClientOpId: entry?.inFlight?.clientOpId ?? null,
    pendingCount: entry?.pending.length ?? 0,
  };
}

/**
 * キューの依頼先を設定する。`null` を渡すと購読とタイマーを解除し、待機列も破棄する
 * （canvas 切替・破棄時に別 canvas へ送らないため）。
 */
export function configureTableOpQueue(next: TableOpQueueHost | null): void {
  host = next;
  if (next === null) {
    resetTableOpQueues();
    return;
  }
  ensureDocStoreSubscription();
  // 依頼先が無い間に積まれた待機列を、ここで送り始める（送られないまま滞留させない）。
  for (const objectId of [...queues.keys()]) pump(objectId);
}

/** すべての待機状態を破棄する（canvas 切替・テストの後始末）。 */
export function resetTableOpQueues(): void {
  queues.clear();
  stopDeadlineTimer();
  unsubscribeDocStore?.();
  unsubscribeDocStore = null;
}

/**
 * op を待機列へ積む。
 *
 * 同じ構造 revision の連続区間で、同一セルへの未送信更新だけを最新値へ畳む。
 * 構造更新を越えてセル更新を統合せず、セルが異なる更新と構造更新は受け付けた順に保持する。
 * 上限に達している場合は積まずに false を返す（呼び出し側は楽観適用しない）。
 */
export function enqueueTableOp(op: TableQueuedOp): boolean {
  const entry = queues.get(op.objectId) ?? { inFlight: null, deadlineAt: null, pending: [] };
  queues.set(op.objectId, entry);
  const mergedIndex = op.type === "object.table.cell.update" ? findMergeTarget(entry, op) : -1;
  if (mergedIndex >= 0) {
    const [dropped] = entry.pending.splice(mergedIndex, 1);
    entry.pending.push(op);
    // 畳んだ古い op は送らないため、楽観適用も取り消して in-flight に残さない。
    if (dropped) host?.dropOptimistic(dropped.clientOpId);
    pump(op.objectId);
    return true;
  }
  if (entry.pending.length >= TABLE_QUEUE_MAX_PENDING) {
    host?.notify(
      "表の未送信の変更が多すぎます。通信が回復して保存が進むまで、少し待ってからお試しください。"
    );
    return false;
  }
  entry.pending.push(op);
  pump(op.objectId);
  return true;
}

/**
 * 対象の表に関する待機列を破棄する（表の削除・ロック・参照専用への遷移）。
 * 送信済みで応答待ちの op は、サーバの確定・拒否に委ねるためここでは触らない。
 */
export function clearTableOpQueue(objectId: string): void {
  const entry = queues.get(objectId);
  if (!entry) return;
  // 取り消しの通知は doc store を更新して購読を呼び戻すため、先に待機列を空にしてから通知する
  // （再入した購読が、捨てたはずの op を送り出すのを防ぐ）。
  const discarded = entry.pending;
  entry.pending = [];
  if (entry.inFlight === null) queues.delete(objectId);
  for (const op of discarded) host?.dropOptimistic(op.clientOpId);
}

/**
 * 表の op がサーバに拒否されたことを伝える（`op.reject` の受信経路から呼ぶ）。
 *
 * 拒否は「その表に対する自分の想定が最新でない」ことを意味する（構造 revision の不一致・版競合・
 * 上限超過など）。待機列の op は同じ想定で組み立てられているため、続けて送ると同じ理由で拒否される
 * だけでなく、権威スナップショットの取り直しより先に古い位置へ書き込もうとする。そこで後続送信を
 * 止め、未送信 op を楽観適用ごと破棄する（§9.2.1）。
 *
 * 拒否された op 自身の楽観適用は、受信経路の `onServerReject` が巻き戻すためここでは触らない。
 * 再同期も既存の `op.reject` 経路が行う。
 */
export function notifyTableOpRejected(clientOpId: string): void {
  for (const [objectId, entry] of queues) {
    if (entry.inFlight?.clientOpId !== clientOpId) continue;
    entry.inFlight = null;
    entry.deadlineAt = null;
    // 取り消しの通知は doc store を更新して購読を呼び戻すため、先に待機列を片付けてから通知する。
    const discarded = entry.pending;
    entry.pending = [];
    queues.delete(objectId);
    for (const op of discarded) host?.dropOptimistic(op.clientOpId);
    stopDeadlineTimerIfIdle();
    return;
  }
}

/** 統合対象（同じ構造 revision の連続区間にある、同一セルへの未送信更新）の位置を返す。 */
function findMergeTarget(entry: QueueEntry, op: UpdateTableCellOp): number {
  for (let index = entry.pending.length - 1; index >= 0; index -= 1) {
    const candidate = entry.pending[index];
    if (!candidate) continue;
    // 構造更新を越えて統合しない（越えると、行・列の増減の前後で位置の意味が変わる）。
    if (candidate.type !== "object.table.cell.update") return -1;
    if (
      candidate.row === op.row &&
      candidate.column === op.column &&
      candidate.structureRevision === op.structureRevision
    ) {
      return index;
    }
  }
  return -1;
}

/** 送信できる状態なら、待機列の先頭を 1 件だけ送る。 */
function pump(objectId: string): void {
  const entry = queues.get(objectId);
  if (!entry || !host) return;
  if (entry.inFlight !== null) return;
  const next = entry.pending[0];
  if (next === undefined) {
    queues.delete(objectId);
    stopDeadlineTimerIfIdle();
    return;
  }
  // 作成直後は、その表の object.create の確定も先行依存として待つ
  // （create より先に着いた表更新は、対象不在として破棄されるため）。
  if (isCreatePending(objectId)) return;
  entry.pending.shift();
  entry.inFlight = next;
  entry.deadlineAt = Date.now() + TABLE_QUEUE_RESPONSE_TIMEOUT_MS;
  ensureDeadlineTimer();
  host.send(next);
}

/** 対象の表の object.create が、まだ確定していない（in-flight に残っている）か。 */
function isCreatePending(objectId: string): boolean {
  return useCanvasDocStore
    .getState()
    .inFlightOps.some((op) => op.type === "object.create" && op.object.id === objectId);
}

/**
 * doc store の in-flight から消えた（＝確定または拒否された）in-flight op を検出し、次を送る。
 * commit / reject のどちらでも doc store が in-flight から取り除くため、1 か所で両方を扱える。
 */
function ensureDocStoreSubscription(): void {
  if (unsubscribeDocStore) return;
  unsubscribeDocStore = useCanvasDocStore.subscribe((state) => {
    const pumpTargets: string[] = [];
    for (const [objectId, entry] of queues) {
      const inFlight = entry.inFlight;
      if (inFlight === null) {
        // 表の object.create 待ちで送り出せずにいる待機列も、確定の契機で送り始められるか試す。
        if (entry.pending.length > 0) pumpTargets.push(objectId);
        continue;
      }
      if (state.inFlightOps.some((op) => op.clientOpId === inFlight.clientOpId)) continue;
      entry.inFlight = null;
      entry.deadlineAt = null;
      pumpTargets.push(objectId);
    }
    for (const objectId of pumpTargets) pump(objectId);
    stopDeadlineTimerIfIdle();
  });
}

function ensureDeadlineTimer(): void {
  if (deadlineTimer !== null || typeof window === "undefined") return;
  deadlineTimer = setInterval(checkDeadlines, TABLE_QUEUE_DEADLINE_TICK_MS);
}

function stopDeadlineTimer(): void {
  if (deadlineTimer === null) return;
  clearInterval(deadlineTimer);
  deadlineTimer = null;
}

function stopDeadlineTimerIfIdle(): void {
  for (const entry of queues.values()) {
    if (entry.inFlight !== null) return;
  }
  stopDeadlineTimer();
}

/**
 * 応答期限を過ぎた表の後続送信を止め、権威スナップショットの取り直しへ移る。
 * 待機列を無条件で最新 revision へ付け替えて再送しない（位置の同一性を確認できないため）。
 *
 * 応答が届かなかった op は、保存できたかどうかを判断できない。楽観適用をそのまま残すと
 * 「保存された」という誤った表示が resync 後も居座るため、未送信分と同じく楽観適用を取り消し、
 * 権威スナップショットの内容へ収束させる（実際には保存できていた場合、その内容は
 * スナップショットまたは後続の op.commit で戻る）。
 */
function checkDeadlines(): void {
  const now = Date.now();
  let expired = false;
  const discarded: TableQueuedOp[] = [];
  for (const [objectId, entry] of queues) {
    if (entry.inFlight === null || entry.deadlineAt === null || entry.deadlineAt > now) continue;
    expired = true;
    discarded.push(entry.inFlight);
    entry.inFlight = null;
    entry.deadlineAt = null;
    discarded.push(...entry.pending);
    entry.pending = [];
    queues.delete(objectId);
  }
  // 取り消しの通知は購読を呼び戻すため、待機列をすべて片付けてから行う。
  for (const op of discarded) host?.dropOptimistic(op.clientOpId);
  if (!expired) {
    stopDeadlineTimerIfIdle();
    return;
  }
  host?.notify("表の変更を保存できたか確認できませんでした。最新の内容を取得し直します。");
  host?.requestResync();
  stopDeadlineTimerIfIdle();
}
